import React, { useEffect, useState } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import Grid from '@material-ui/core/Grid';

import { accountSelector, accountsStatusSelector } from '../../../account/account-selectors';
import { loadAccount } from '../../../account/actions/load-account';
import { openOrCloseGlobalDialog } from '../../../action/dialog/global-dialog';
import { setAccountStatus } from '../../actions/set-account-status';

import { GLOBAL_DIALOGS } from '../../../dialogs/global-dialogs-config';
import { UiButton } from '../../../ui/ui-button';

import { CancelSubscriptionButton } from './status-transition-dialogs/cancel-subscription-button';

import { ACCOUNT_STATUS, ACCOUNT_STATUS_CONFIG } from '../../../util/account-status';

type InputProps = {
  accountId: number;
  org?: Org;
  onTransition?: (status: AccountStatus) => void;
};

export const AccountStatusTransitions = ({ accountId, org, onTransition }: InputProps) => {
  const [selectedStatus, setSelectedStatus] = useState<AccountStatus | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const account = useSelector((state: any) => accountSelector(state, accountId), shallowEqual);
  // Listen to status updates so this component updates when account is set dirty
  useSelector((state: any) => accountsStatusSelector(state, accountId));

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAccount(accountId));
  });

  if (!account) {
    return null;
  }

  const makeStatusTransition = async (status: AccountStatus, extraInfo = {}) => {
    if (account.status === ACCOUNT_STATUS.CLOSED) {
      dispatch(openOrCloseGlobalDialog(GLOBAL_DIALOGS.REACTIVATE_USERS_DIALOG.key, true));
    }
    await dispatch(setAccountStatus(accountId, status, extraInfo));
    if (onTransition) {
      onTransition(status);
    }
    setSubmitting(false);
  };

  const handleTransitionConfirmed = async (extraInfo = {}) => {
    if (!selectedStatus) {
      return;
    }
    await makeStatusTransition(selectedStatus, extraInfo);
  };

  const selectStatus = (status: AccountStatus) => async () => {
    const { TransitionComponent } = ACCOUNT_STATUS_CONFIG[status];

    if (TransitionComponent) {
      setSelectedStatus(status);
      setSubmitting(true);
    } else {
      setSubmitting(true);
      await makeStatusTransition(status);
    }
  };

  const cancelTransition = () => {
    setSelectedStatus(null);
    setSubmitting(false);
  };

  const currentStatusConfiguration = ACCOUNT_STATUS_CONFIG[account.status];
  const selectedStatusConfiguration = selectedStatus ? ACCOUNT_STATUS_CONFIG[selectedStatus] : null;

  const { transitions } = currentStatusConfiguration;
  const TransitionComponent = selectedStatusConfiguration?.TransitionComponent;

  return (
    <>
      <Grid container spacing={1} justifyContent="center">
        {transitions.map((s, i) => (
          <Grid item key={s.status}>
            {s.status === ACCOUNT_STATUS.CANCELED ? (
              <CancelSubscriptionButton accountId={account.accountId} />
            ) : (
              <UiButton
                variant={
                  i === 0 ? 'secondary' : i === transitions.length - 1 ? 'primary' : 'dismiss'
                }
                onClick={selectStatus(s.status)}
                disabled={submitting}
              >
                {s.text}
              </UiButton>
            )}
          </Grid>
        ))}
      </Grid>
      {TransitionComponent ? (
        <TransitionComponent
          account={account}
          org={org}
          handleTransitionConfirmed={handleTransitionConfirmed}
          cancelTransition={cancelTransition}
        />
      ) : null}
    </>
  );
};
